// filepath: apps/web/src/features/admin/recordPermissions/RecordFieldPermissionsRoute.tsx
//
// FR-UI-ADM-2 AC-2 — route shell. Reads `environmentKind` from the
// cached `tenant-config` query (FR-UI-CORE-2). Renders
// StagingOnlyError when environmentKind !== "staging" — defense-
// in-depth alongside the server-side HTTP 403 ENV_PRODUCTION gate.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useTenantConfig } from "@/bootstrap/tenantConfig";
import { useTenant } from "@/tenant/TenantProvider";
import { StagingOnlyError } from "@/features/admin/StagingOnlyError";
import { RecordFieldPermissionsPage } from "./RecordFieldPermissionsPage";

export function RecordFieldPermissionsRoute(): React.ReactElement {
  const { t } = useTranslation();
  const { tenantSlug } = useTenant();
  const tenantConfig = useTenantConfig(tenantSlug);

  if (tenantConfig.isPending) {
    return (
      <div
        className="p-4 text-xs text-muted-foreground"
        data-testid="record-permissions-route-loading"
      >
        {t("common.loading")}
      </div>
    );
  }

  if (tenantConfig.error || !tenantConfig.data) {
    return (
      <div className="p-4" role="alert">
        <h2 className="text-sm font-semibold">
          {t("admin.errors.staging_only.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          {tenantConfig.error?.message ?? "tenant-config unavailable"}
        </p>
      </div>
    );
  }

  // TenantConfig shape is snake_case at the wire boundary (per
  // TenantConfig interface in auth/types.ts). Normalize to lowercase
  // for the staging-only gate (mirrors FR-UI-ADM-1's pattern).
  const environmentKind = (
    tenantConfig.data.environment_kind ?? "staging"
  ).toLowerCase();

  if (environmentKind !== "staging") {
    return (
      <StagingOnlyError
        environmentKind={environmentKind}
        message={t("admin.record_permissions.errors.staging_only_body")}
      />
    );
  }

  return <RecordFieldPermissionsPage />;
}
